import { ImagePlus, Pencil, Play, Trash2 } from 'lucide-react';

import { Button, cn } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { A2vAsset } from '../../../assets/model';
import { A2vOverlayPosition, A2vTemplateType, type A2vConfiguration } from '../../model';

interface NetworkTemplatePreviewProps {
  configuration: A2vConfiguration;
  qrCodeAsset: string | null;
  disabled: boolean;
  onChooseAssets: () => void;
  onRemoveAsset: (asset: A2vAsset) => void;
}

export function NetworkTemplatePreview({
  configuration,
  qrCodeAsset,
  disabled,
  onChooseAssets,
  onRemoveAsset,
}: NetworkTemplatePreviewProps) {
  const videoTemplate = configuration.templateType === A2vTemplateType.Video;
  const previewAsset = videoTemplate ? configuration.video : configuration.images[0];
  let previewContent = (
    <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-3">
      {videoTemplate ? (
        <Play className="h-10 w-10" aria-hidden="true" />
      ) : (
        <ImagePlus className="h-10 w-10" aria-hidden="true" />
      )}
      <span className="text-sm">{dashboardT('a2v.networkTemplate.previewEmpty')}</span>
    </div>
  );
  if (previewAsset?.url && videoTemplate) {
    previewContent = (
      <video
        className="h-full w-full object-contain"
        src={previewAsset.url}
        controls
        muted
        preload="metadata"
      />
    );
  } else if (previewAsset?.url) {
    previewContent = (
      <img
        className="h-full w-full object-contain"
        src={previewAsset.url}
        alt={previewAsset.name}
      />
    );
  }

  return (
    <section className="border-border bg-card min-w-0 overflow-hidden rounded-md border shadow">
      <div className="border-border flex min-h-[53px] flex-wrap items-center justify-between gap-2 border-b px-5 py-3">
        <h2 className="text-base font-semibold">{dashboardT('a2v.networkTemplate.preview')}</h2>
      </div>
      <div
        className="relative aspect-video overflow-hidden bg-black sm:min-h-[260px]"
        data-testid="network-template-preview-stage"
      >
        {previewContent}
        {configuration.logoEnabled && configuration.logo?.url ? (
          <img
            className={cn(
              'absolute max-h-[18%] max-w-[25%] object-contain drop-shadow-md',
              overlayPositionClassName[configuration.logoPosition],
            )}
            src={configuration.logo.url}
            alt={dashboardT('a2v.networkTemplate.logo')}
          />
        ) : null}
        {configuration.qrCodeEnabled && qrCodeAsset ? (
          <img
            className={cn(
              'absolute h-[14%] min-h-10 w-auto bg-white p-1 shadow-md',
              overlayPositionClassName[configuration.qrCodePosition],
            )}
            src={qrCodeAsset}
            alt={dashboardT('a2v.networkTemplate.qrCode')}
          />
        ) : null}
      </div>
      <div className="border-border flex min-w-0 flex-wrap items-center justify-between gap-3 border-t px-5 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{previewAsset?.name ?? '-'}</p>
          <p className="text-muted-foreground text-xs">
            {previewAsset ? `${String(previewAsset.width)} x ${String(previewAsset.height)}` : '-'}
          </p>
        </div>
        <Button type="button" variant="outline" disabled={disabled} onClick={onChooseAssets}>
          <Pencil className="h-4 w-4" aria-hidden="true" />
          {dashboardT(
            videoTemplate ? 'a2v.networkTemplate.changeVideo' : 'a2v.networkTemplate.changeImages',
          )}
        </Button>
      </div>
      {!videoTemplate && configuration.images.length > 0 ? (
        <div className="border-border grid grid-cols-3 gap-2 border-t px-5 py-3 sm:grid-cols-5 lg:grid-cols-6">
          {configuration.images.map((asset) => (
            <div
              className="border-input bg-muted group relative aspect-video overflow-hidden rounded-md border"
              key={asset.id}
            >
              {asset.url ? (
                <img className="h-full w-full object-cover" src={asset.url} alt={asset.name} />
              ) : null}
              <Button
                type="button"
                variant="destructive"
                size="icon"
                disabled={disabled}
                className="absolute top-1 right-1 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                aria-label={dashboardT('a2v.networkTemplate.removeAsset', { name: asset.name })}
                onClick={() => {
                  onRemoveAsset(asset);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

const overlayPositionClassName = {
  [A2vOverlayPosition.TopLeft]: 'top-4 left-4',
  [A2vOverlayPosition.TopRight]: 'top-4 right-4',
  [A2vOverlayPosition.BottomLeft]: 'bottom-4 left-4',
  [A2vOverlayPosition.BottomRight]: 'right-4 bottom-4',
} as const;
